import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useLogin } from '@/hooks/authHooks'
import { resolveAuth } from '@/lib/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'

import AuthCard from '@/components/auth/AuthCard'
import AuthForm from '@/components/auth/AuthForm'
import SocialButtons from '@/components/auth/SocialButtons'
import ImageSection from '@/components/auth/ImageSection'

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

export const Route = createFileRoute('/_regular/login/')({
  validateSearch: (search) => ({
    redirect: search.redirect || '/dashboard',
  }),
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const auth = await resolveAuth(queryClient)
    if (auth.isAuthenticated) {
      throw redirect({ to: search.redirect })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { mutateAsync: login, isLoading } = useLogin()
  const [serverError, setServerError] = useState('')
  const navigate = Route.useNavigate()
  const { redirect } = Route.useSearch()

  const {
    control,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (values) => {
    setServerError('')
    try {
      await login(values)
      navigate({ to: redirect })
    } catch (err) {
      setServerError(err.response?.data?.message || 'فشل تسجيل الدخول')
    }
  }

  const handleSocialLogin = (provider) => {
    alert(`سيتم تفعيل تسجيل الدخول عبر ${provider} قريباً`)
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col md:flex-row">
        <div className="relative overflow-hidden order-2 md:order-1 w-full md:w-1/2 flex items-center justify-center p-6 md:p-12">

          <AuthCard
            title="تسجيل الدخول"
            description="أدخل بياناتك للدخول إلى حسابك"
          >
            <AuthForm
              mode="login"
              control={control}
              loading={isLoading}
              onSubmit={handleSubmit(onSubmit)}
              forgotPasswordLink={
                <Link to="/forgot-password" className="text-primary hover:underline">
                  نسيت كلمة المرور؟
                </Link>
              }
            />

            {serverError && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <Separator className="my-6" />

            <SocialButtons onSocialClick={handleSocialLogin} />

            <div className="mt-6 text-center text-sm">
              ليس لديك حساب؟{' '}
              <Link to="/register" className="text-primary hover:underline">
                إنشاء حساب جديد
              </Link>
            </div>
          </AuthCard>
        </div>

        <div className="order-1 md:order-2 w-full md:w-1/2 h-64 md:h-auto">
          <ImageSection />
        </div>
      </div>
    </div>
  )
}